/* istanbul ignore file */
import { alpha, extendTheme } from '@mui/material/styles';
import { foundationColors, foundationMuiColorValues } from './foundationColors';

const COLOR_PALETTE = {
  primary: {
    main: foundationMuiColorValues.primary,
  },
  secondary: {
    main: foundationMuiColorValues.secondary,
  },
  info: {
    main: foundationMuiColorValues.info,
    dark: foundationColors.infoDark,
    light: foundationColors.infoLight,
  },
  neutral: {
    main: foundationColors.textSecondary,
    contrastText: foundationColors.white,
  },
  error: {
    main: foundationMuiColorValues.error,
    dark: foundationColors.errorDark,
  },
  success: {
    main: foundationMuiColorValues.success,
    light: foundationColors.successLight,
    bpmn: foundationColors.successBpmn,
  },
  warning: {
    main: foundationMuiColorValues.warning,
  },
};

const TYPOGRAPHY_COLORS = {
  ...foundationMuiColorValues,
  textPrimary: foundationColors.textPrimary,
  textSecondary: foundationColors.textSecondary,
  textDisabled: foundationColors.disabled,
  brand: foundationColors.brand,
  textWhite: foundationColors.white,
} as const;

const lightPinkColorPalette = {
  ...COLOR_PALETTE,
  brand: foundationColors.brand,
  text: {
    primary: foundationColors.textPrimary,
    secondary: foundationColors.textSecondary,
    disabled: foundationColors.disabled,
    success: foundationMuiColorValues.success,
    warning: foundationMuiColorValues.warning,
    error: foundationMuiColorValues.error,
  },
  border: {
    default: foundationColors.borderDefault,
    input: foundationColors.borderInput,
    separator: foundationColors.borderSeparator,
    header: foundationColors.borderHeader,
    medium: foundationColors.borderMedium,
    strong: foundationColors.borderStrong,
    contrast: foundationColors.borderContrast,
    success: foundationMuiColorValues.success,
    warning: foundationMuiColorValues.warning,
    error: foundationMuiColorValues.error,
  },
  background: {
    paper: foundationColors.white,
    primary: foundationColors.brandSubtle,
    secondary: foundationColors.secondarySurface,
    brandSubtle: foundationColors.brandSubtle,
    success: foundationColors.successSurface,
    warning: foundationColors.warningSurface,
    info: foundationColors.infoSurface,
    error: foundationColors.errorSurface,
    default: foundationColors.pageDefault,
  },
  common: {
    white: foundationColors.white,
    menuItemActive: foundationColors.menuItemActive,
    commonInputBorder: foundationColors.borderInput,
    tooltipBg: foundationColors.tooltipSurface,
    inputColor: foundationColors.textPrimary,
    inputDisabledBackground: foundationColors.inputDisabledSurface,
    sidebarMenuActive: foundationColors.sidebarActiveSurface,
    sidebarMenuBg: foundationColors.sidebarSurface,
    sidebarMenuText: foundationColors.white,
    sidebarBorder: foundationColors.borderStrong,
    separator: foundationColors.borderSeparator,
    softShadow: foundationColors.softShadow,
    softBorder: foundationColors.softBorder,
    shadowMedium: foundationColors.shadowMedium,
    shadowDefault: foundationColors.shadowDefault,
    shadowStrong: foundationColors.shadowStrong,
    shadowContrast: foundationColors.shadowContrast,
    loadingOverlay: foundationColors.loadingOverlay,
    transparentWhite: foundationColors.transparentWhite,
    scrollbarLightGrey: foundationColors.scrollbarLightThumb,
    deletedTableRow: foundationColors.deletedTableRowSurface,
    tableRowHover: foundationColors.tableRowHoverSurface,
    inputIcons: foundationColors.inputIcon,
  },
  timeline: {
    feedback: foundationColors.timelineFeedback,
    strategy: foundationColors.timelineStrategy,
    note: foundationColors.timelineNote,
    email: foundationColors.timelineEmail,
    call: foundationColors.timelineCall,
    meeting: foundationColors.timelineMeeting,
    demo: foundationColors.timelineDemo,
    planning: foundationColors.timelinePlanning,
    skipped: foundationColors.timelineSkipped,
  },
  action: {
    disabled: foundationColors.disabled,
  },
};

export const violetTheme = extendTheme({
  breakpoints: {
    values: {
      xs: 0,
      smallMobile: 600,
      sm: 768,
      md: 990,
      lg: 1195,
      xl: 1440,
      xxl: 1500,
      xxxl: 1681,
    },
  },
  typography: {
    fontFamily: "'Open Sans', sans-serif",
    fontSize: 14,
    h1: {
      fontSize: 32,
      lineHeight: '40px',
      fontWeight: 400,
      textTransform: 'none',
    },
    h2: {
      fontSize: 28,
      lineHeight: '36px',
      fontWeight: 400,
      textTransform: 'none',
    },
    h3: {
      fontSize: 24,
      lineHeight: '32px',
      fontWeight: 400,
      textTransform: 'none',
    },
    h4: {
      fontSize: 20,
      lineHeight: '28px',
      fontWeight: 400,
      textTransform: 'none',
    },
    h5: {
      fontSize: 18,
      lineHeight: '24px',
      fontWeight: 600,
      textTransform: 'none',
    },
    h6: {
      fontSize: 16,
      lineHeight: '22px',
      fontWeight: 600,
      textTransform: 'none',
    },
    subtitle1: {
      fontSize: 14,
      lineHeight: '22px',
      fontWeight: 500,
      textTransform: 'none',
    },
    subtitle2: {
      fontSize: 12,
      lineHeight: '18px',
      fontWeight: 600,
      textTransform: 'none',
    },
    caption: {
      fontSize: 10,
      lineHeight: '18px',
      fontWeight: 400,
      textTransform: 'none',
    },
    body1: {
      fontSize: 14,
      lineHeight: '22px',
      fontWeight: 400,
      textTransform: 'none',
    },
    body2: {
      fontSize: 12,
      lineHeight: '18px',
      fontWeight: 400,
      textTransform: 'none',
    },
    display0: {
      fontSize: 80,
      lineHeight: '80px',
      fontWeight: 400,
      textTransform: 'none',
    },
  },
  colorSchemes: {
    light: {
      palette: lightPinkColorPalette,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: "'Open Sans', sans-serif",
        },
        'h1, h2, h3, h4, h5': {
          margin: 0,
        },
        '@supports selector(::-webkit-scrollbar) and (not (font: -apple-system-body))': {
          '*::-webkit-scrollbar': {
            width: '5px',
            height: '5px',
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
            borderRadius: '4px',
          },
          '*::-webkit-scrollbar-corner': {
            backgroundColor: 'transparent',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: foundationColors.scrollbarLightThumb,
            borderRadius: '4px',
          },
          'html::-webkit-scrollbar, body::-webkit-scrollbar': {
            width: '9px',
            height: '9px',
          },
          'html::-webkit-scrollbar-thumb, body::-webkit-scrollbar-thumb': {
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
            borderRadius: '5px',
          },
        },
        '@supports (not selector(::-webkit-scrollbar)) or (font: -apple-system-body)': {
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: `${foundationColors.scrollbarLightThumb} transparent`,
          },
        },
        table: {
          borderCollapse: 'collapse',
        },
        'table.MuiTable-root': {
          width: '100%',
          '@media (max-width: 1499.98px)': {
            minWidth: '1480px',
          },
        },
        '.checkbox-form-group__column .MuiFormControl-root': {
          marginBottom: 0,
        },
        '.Mui-disabled': {
          cursor: 'not-allowed',
        },
        '.form-group .form-error': {
          display: 'block',
        },
        a: {
          textDecoration: 'none',
          color: foundationMuiColorValues.primary,
        },
        'a:hover': {
          textDecoration: 'underline',
        },
        'input[type="search"]': {
          WebkitAppearance: 'textfield',
        },
        'input[type="search"]::-webkit-search-decoration': {
          WebkitAppearance: 'none',
        },
        '.form-control': {
          fontFamily: "'Open Sans', sans-serif",
          display: 'block',
          width: '100%',
          padding: '0.5rem 0.75rem',
          fontSize: '0.875rem',
          fontWeight: 400,
          lineHeight: 1.5,
          color: foundationColors.textPrimary,
          backgroundColor: foundationColors.white,
          border: 'none',
          borderRadius: '4px !important',
          transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
        },
        '.form-control:focus, .form-control:focus-visible': {
          outline: 'none',
          boxShadow: 'none',
        },
        '.form-control.is-invalid': {
          borderColor: foundationMuiColorValues.error,
        },
        '.form-control.is-invalid:focus': {
          borderColor: foundationMuiColorValues.error,
          boxShadow: `0 0 0 1px ${foundationMuiColorValues.error}`,
        },
        '.form-control:disabled': {
          cursor: 'not-allowed',
          borderColor: foundationColors.borderInput,
          opacity: 0.38,
          backgroundColor: foundationColors.inputDisabledSurface,
        },
        '.rdt.is-invalid .form-control': {
          borderColor: foundationMuiColorValues.error,
        },
        '.rdt.is-invalid .form-control:focus': {
          borderColor: foundationMuiColorValues.error,
        },
        '[type="button"]:not(:disabled), [type="reset"]:not(:disabled), [type="submit"]:not(:disabled), button:not(:disabled)':
          {
            cursor: 'pointer',
          },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: '20px',
          '&.checkbox-form-group, &.instance-feedback__task-checkbox': {
            marginTop: 0,
          },
          '&.checkbox-form-group.checkbox-form-group--with-margin, &.instance-feedback__task-checkbox.checkbox-form-group--with-margin':
            {
              '@media (min-width: 990px)': {
                marginTop: '22px',
              },
              '@media (max-width: 989.98px)': {
                marginBottom: '12px',
              },
            },
        },
      },
    },
    MuiTypography: {
      variants: [
        {
          props: { color: 'textPrimary' },
          style: { color: TYPOGRAPHY_COLORS.textPrimary },
        },
        {
          props: { color: 'textSecondary' },
          style: { color: TYPOGRAPHY_COLORS.textSecondary },
        },
        {
          props: { color: 'textDisabled' },
          style: { color: TYPOGRAPHY_COLORS.textDisabled },
        },
        {
          props: { color: 'brand' },
          style: { color: TYPOGRAPHY_COLORS.brand },
        },
        {
          props: { color: 'textWhite' },
          style: { color: TYPOGRAPHY_COLORS.textWhite },
        },
      ],
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: foundationColors.white,
          borderBottom: `1px solid ${foundationColors.borderHeader}`,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        variant: 'contained',
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          flexShrink: 0,
          '@media (max-width: 767px)': {
            padding: '3px 7px',
          },
        },
        outlined: {
          '&, &:hover': {
            borderColor: foundationColors.borderMedium,
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          transition: 'none !important',
          wordWrap: 'break-word',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          padding: '12px',
          '&:first-child': {
            paddingLeft: '8px',
          },
          '&:last-child': {
            paddingRight: '8px',
          },
        },
        sizeSmall: { padding: '6px 16px' },
        head: {
          color: COLOR_PALETTE.primary.main,
          fontSize: '12px',
          lineHeight: '1.5',
          '& .Mui-active': {
            color: COLOR_PALETTE.primary.main,
          },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          lineHeight: '1.5',
          marginRight: '-6px',
          '&.Mui-active': {
            color: COLOR_PALETTE.primary.main,
            '& .MuiTableSortLabel-icon': {
              fill: COLOR_PALETTE.primary.main,
            },
            '&: hover': {
              opacity: 0.8,
            },
          },
          '& .MuiSvgIcon-root': { opacity: 0.4, fontSize: '16px', fill: foundationColors.textSecondary },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '11px',
          lineHeight: '1.4',
          marginLeft: 0,
          marginRight: 0,
          '&:not(:empty)': {
            marginBottom: '2px',
          },
          '&:empty': {
            marginTop: 0,
          },
        },
      },
    },
    MuiChip: {
      variants: [
        {
          props: { color: 'primary' },
          style: {
            backgroundColor: lightPinkColorPalette.background.primary,
            color: COLOR_PALETTE.primary.main,
            '&.MuiChip-clickable:hover': {
              backgroundColor: alpha(COLOR_PALETTE.primary.main, 0.16),
            },
          },
        },
        {
          props: { color: 'secondary' },
          style: {
            backgroundColor: lightPinkColorPalette.background.secondary,
            color: COLOR_PALETTE.secondary.main,
            '&.MuiChip-clickable:hover': {
              backgroundColor: alpha(COLOR_PALETTE.secondary.main, 0.16),
            },
          },
        },
        {
          props: { color: 'success' },
          style: {
            backgroundColor: lightPinkColorPalette.background.success,
            color: COLOR_PALETTE.success.main,
            '&.MuiChip-clickable:hover': {
              backgroundColor: alpha(COLOR_PALETTE.success.main, 0.16),
            },
          },
        },
        {
          props: { color: 'warning' },
          style: {
            backgroundColor: lightPinkColorPalette.background.warning,
            color: COLOR_PALETTE.warning.main,
            '&.MuiChip-clickable:hover': {
              backgroundColor: alpha(COLOR_PALETTE.warning.main, 0.16),
            },
          },
        },
        {
          props: { color: 'info' },
          style: {
            backgroundColor: lightPinkColorPalette.background.info,
            color: COLOR_PALETTE.info.main,
            '&.MuiChip-clickable:hover': {
              backgroundColor: alpha(COLOR_PALETTE.info.main, 0.16),
            },
          },
        },
        {
          props: { color: 'error' },
          style: {
            backgroundColor: lightPinkColorPalette.background.error,
            color: COLOR_PALETTE.error.main,
            '&.MuiChip-clickable:hover': {
              backgroundColor: alpha(COLOR_PALETTE.error.main, 0.16),
            },
          },
        },
      ],
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          '& > .MuiGrid-item': {
            paddingTop: 0,
          },
        },
        container: {
          marginTop: 0,
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {
          color: foundationColors.borderMedium,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        message: {
          fontSize: '14px',
        },
        action: {
          paddingTop: '2px',
        },
        icon: {
          alignItems: 'center',
          marginRight: '14px',
        },
      },
    },
    MuiSvgIcon: {
      variants: [
        {
          props: { color: 'success' },
          style: {
            color: foundationColors.successLight,
          },
        },
        {
          props: { color: 'error.dark' },
          style: {
            color: foundationColors.errorDark,
          },
        },
      ],
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: foundationColors.borderInput,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: foundationColors.textPrimary,
        },
        filled: {
          color: foundationColors.textPrimary,
        },
      },
    },
  },
});
